import React from "react";
import { formatPrice, siteConfig } from "../Datas/productData";
import PolicyNav, { POLICY_UPDATED } from "./PolicyNav";
import "../css/delivery.scss";

function Delivery() {
  const shippingRate = formatPrice(siteConfig.shipping.standardRate);

  return (
    <main className="policy-page delivery">
      <header className="policy-hero">
        <p className="policy-eyebrow">Customer care</p>
        <h1>Shipping &amp; delivery</h1>
        <p>
          How we process, track, and deliver your {siteConfig.brandName} order.
        </p>
        <span>Last updated {POLICY_UPDATED}</span>
      </header>

      <div className="policy-layout">
        <PolicyNav current="/shipping" />

        <article className="policy-content">
          <section className="policy-intro" aria-labelledby="delivery-overview">
            <p className="policy-eyebrow">At a glance</p>
            <h2 id="delivery-overview">Tracked from checkout to your door.</h2>
            <p>
              We normally prepare orders in 1–2 business days. Shipping is a
              flat {shippingRate} for destinations available at checkout, and
              delivery estimates begin after your order is dispatched.
            </p>
          </section>

          <section className="policy-cards" aria-label="Delivery highlights">
            <div>
              <span>01</span>
              <h3>1–2 days</h3>
              <p>Normal processing time, excluding weekends and holidays.</p>
            </div>
            <div>
              <span>02</span>
              <h3>Tracked</h3>
              <p>We email tracking information when the parcel is dispatched.</p>
            </div>
            <div>
              <span>03</span>
              <h3>{shippingRate}</h3>
              <p>Flat-rate shipping for destinations offered at checkout.</p>
            </div>
          </section>

          <section className="policy-section" aria-labelledby="shipping-processing">
            <div className="policy-section__heading">
              <span>01</span>
              <h2 id="shipping-processing">Processing &amp; order changes</h2>
            </div>
            <p>
              Orders are processed Monday through Friday, excluding U.S.
              federal holidays. High-volume launches, promotions, severe
              weather, or carrier disruption may add processing time. If a
              material delay affects your order, we will use the email address
              provided at checkout to update you.
            </p>
            <p>
              Check your delivery information carefully before payment. Contact
              us immediately if it needs to be changed. We cannot guarantee an
              amendment or cancellation once an order has entered processing or
              has been handed to the carrier.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="shipping-estimates">
            <div className="policy-section__heading">
              <span>02</span>
              <h2 id="shipping-estimates">Rates &amp; delivery estimates</h2>
            </div>
            <p>
              The available destination, service, charge, and current estimate
              are shown during checkout. The following ranges are typical, not
              guaranteed delivery dates.
            </p>
            <div
              className="policy-table-wrap"
              tabIndex="0"
              role="region"
              aria-label="Shipping estimates; scroll horizontally for more columns"
            >
              <table>
                <caption className="sr-only">Typical tracked shipping estimates</caption>
                <thead>
                  <tr>
                    <th scope="col">Destination</th>
                    <th scope="col">Service</th>
                    <th scope="col">Typical estimate</th>
                    <th scope="col">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">United States</th>
                    <td>Tracked shipping</td>
                    <td>5–12 business days</td>
                    <td>{shippingRate}</td>
                  </tr>
                  <tr>
                    <th scope="row">Canada</th>
                    <td>International tracked</td>
                    <td>7–14 business days</td>
                    <td>{shippingRate}</td>
                  </tr>
                  <tr>
                    <th scope="row">Other available destinations</th>
                    <td>International tracked</td>
                    <td>7–21 business days</td>
                    <td>{shippingRate}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="policy-section" aria-labelledby="shipping-tracking">
            <div className="policy-section__heading">
              <span>03</span>
              <h2 id="shipping-tracking">Tracking &amp; delivery attempts</h2>
            </div>
            <p>
              Tracking may take up to 48 hours to show its first scan. Carriers
              may require a signature, make more than one delivery attempt, or
              route a parcel to a local collection point. Follow the carrier’s
              instructions promptly so the parcel is not returned to us.
            </p>
            <p>
              If a parcel is returned because an address was incomplete,
              delivery was refused, or collection was missed, we may ask you to
              pay the actual cost of reshipping. Any refund instead will exclude
              non-refundable delivery and return-to-sender charges, except where
              applicable law requires otherwise.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="shipping-international">
            <div className="policy-section__heading">
              <span>04</span>
              <h2 id="shipping-international">International orders</h2>
            </div>
            <p>
              The recipient is responsible for import duties, customs fees,
              brokerage charges, and local taxes unless checkout expressly says
              they are included. Customs authorities may request identification
              or other information and can delay a shipment. We cannot mark a
              commercial order as a gift or reduce its declared value.
            </p>
            <p>
              We ship only to destinations offered at checkout. Carrier rules
              may prevent delivery to P.O. boxes, forwarding services, parcel
              lockers, or restricted and remote locations.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="shipping-problems">
            <div className="policy-section__heading">
              <span>05</span>
              <h2 id="shipping-problems">Lost, damaged, or incorrect parcels</h2>
            </div>
            <p>
              If tracking shows no movement for seven business days, shows
              delivered but the parcel cannot be found, or the package arrives
              damaged, email us with your order number and clear photographs
              where relevant. Please report visible damage or an incorrect item
              within seven calendar days of delivery so we can work with the
              carrier and offer an appropriate remedy.
            </p>
            <p>
              Do not discard the product, packaging, or shipping label until we
              confirm that our review is complete. This policy does not limit
              any non-waivable rights available under applicable law.
            </p>
          </section>

          <section className="policy-help" aria-labelledby="shipping-help">
            <div>
              <p className="policy-eyebrow">Need a hand?</p>
              <h2 id="shipping-help">We’ll help you locate your order.</h2>
            </div>
            <a href={`mailto:${siteConfig.contact.email}`}>
              Email customer care <span aria-hidden="true">↗</span>
            </a>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Delivery;
